/* Lightweight GA4/GTM-compatible event dispatcher.
 *
 * All events push to window.dataLayer so GTM can pick them up. If gtag is
 * present (direct GA4 install) they also forward to it. All calls are
 * safe on server / prerender (checks typeof window).
 *
 * Every helper here is idempotent per-render — components should either
 * call trackEvent inside a `useEffect` (so it doesn't fire on every re-
 * render) or wire it to an actual user action (`onClick`, submit, etc.).
 */
const isDev = process.env.NODE_ENV !== "production";

// Simple per-page-load dedupe for view events. Reset when the URL changes
// via the GTM `page_view` listener (see components/common/GTM.jsx).
const firedThisPage = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => firedThisPage.clear());
}

export function trackEvent(name, params = {}) {
  try {
    const g = typeof window !== "undefined" ? window.gtag : null;
    if (typeof g === "function") {
      g("event", name, params);
    } else if (isDev) {
      // eslint-disable-next-line no-console
      console.debug("[analytics]", name, params);
    }
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...params });
    }
  } catch {
    /* swallow — analytics must never break the app */
  }
}

/**
 * Track a "view" event only once per page-load. Use inside useEffect for
 * service_page_view, case_study_view, etc.
 */
export function trackViewOnce(name, params = {}) {
  const key = `${name}::${(typeof window !== "undefined" ? window.location.pathname : "")}`;
  if (firedThisPage.has(key)) return;
  firedThisPage.add(key);
  trackEvent(name, params);
}

/**
 * Track outbound clicks (mailto:, tel:, wa.me/, etc.) without breaking
 * default navigation. Attach as an inline onClick handler:
 *   <a href={`tel:...`} onClick={() => trackClick("phone_clicked", ...)}>
 */
export function trackClick(name, params = {}) {
  trackEvent(name, params);
}
